import { useEffect, useState, useMemo } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Modal from '../../components/ui/Modal.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import SendReminderButton from '../../components/admin/SendReminderButton.jsx';
import UserMultiSelect from '../../components/admin/UserMultiSelect.jsx';
import { cn } from '@/lib/utils';
import { Plus, Upload, Trash2, Pencil, AlertTriangle, Download, Loader2 } from 'lucide-react';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'no_notifiees', label: 'No Notifiees' },
  { value: 'expiring', label: 'Expiring' },
  { value: 'expired', label: 'Expired' },
  { value: 'none', label: 'No File' },
];

// notifyUserIds may arrive as an array (already parsed) or a JSON
// string (raw from Table Storage). Return how many distinct people
// are configured to be emailed/pinged for this doc.
function notifieeCount(value) {
  if (Array.isArray(value)) return value.filter(Boolean).length;
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(Boolean).length : 0;
    } catch {
      return 0;
    }
  }
  return 0;
}

export default function DocumentsPage() {
  const toast = useToast();
  const [docTypes, setDocTypes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all'); // all | org | personal
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // document to edit or { isNew: true }

  async function load() {
    setLoading(true);
    try {
      const [typesRes, docsRes, usersRes] = await Promise.all([
        adminApi.listDocTypes(),
        adminApi.listDocuments(),
        adminApi.listUsers(),
      ]);
      setDocTypes((typesRes.types || []).filter((t) => t.isActive !== false));
      setDocuments(docsRes.documents || []);
      setUsers((usersRes.users || []).filter((u) => u.isActive !== false));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getTypeName = (id) => docTypes.find((t) => t.rowKey === id)?.displayName || id;

  function getStatus(doc) {
    if (!doc.blobPath) return 'none';
    if (!doc.expiryDate) return 'active';
    const today = new Date().toISOString().substring(0, 10);
    if (doc.expiryDate < today) return 'expired';
    const cutoff = new Date(Date.now() + (doc.reminderDays || 30) * 86400000).toISOString().substring(0, 10);
    if (doc.expiryDate <= cutoff) return 'expiring';
    // Doc has an expiry date and is still active, but nobody has been
    // configured to receive the reminder — flag it so the admin notices
    // before the expiry actually arrives.
    if (notifieeCount(doc.notifyUserIds) === 0) return 'no_notifiees';
    return 'active';
  }

  function getDaysUntil(date) {
    if (!date) return null;
    return Math.ceil((new Date(date) - new Date()) / 86400000);
  }

  const filtered = useMemo(() => {
    let list = documents;
    if (filter !== 'all') list = list.filter((d) => getStatus(d) === filter);
    if (typeFilter !== 'all') list = list.filter((d) => d.rowKey === typeFilter || d.docTypeId === typeFilter);
    if (scopeFilter === 'org') list = list.filter((d) => d.partitionKey === 'org');
    else if (scopeFilter === 'personal') list = list.filter((d) => d.partitionKey !== 'org');
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((d) =>
        d.documentName?.toLowerCase().includes(s) ||
        d.fileName?.toLowerCase().includes(s) ||
        d.description?.toLowerCase().includes(s) ||
        d.userName?.toLowerCase().includes(s) ||
        d.userEmail?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [documents, filter, typeFilter, scopeFilter, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const expiringCount = documents.filter((d) => getStatus(d) === 'expiring').length;
  const expiredCount = documents.filter((d) => getStatus(d) === 'expired').length;

  async function handleDelete(doc) {
    if (!confirm(`Delete "${doc.documentName || doc.fileName}"?`)) return;
    try {
      // Use the partitionKey (which is 'org' for org docs) and rowKey
      await adminApi.deleteDocument(doc.partitionKey, doc.rowKey);
      toast.success('Document deleted');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-16"><div className="h-6 w-6 animate-spin rounded-full border-[2.5px] border-border border-t-primary" /></div>;
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Documents</h1>
          <p className="text-muted-foreground">
            Track organizational documents with expiry notifications
            {expiringCount > 0 && <span className="text-warning"> · {expiringCount} expiring</span>}
            {expiredCount > 0 && <span className="text-destructive"> · {expiredCount} expired</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SendReminderButton kinds={['documents']} label="Send document reminders" />
          <Button onClick={() => setEditing({ isNew: true, documentName: '', docTypeId: '', description: '', expiryDate: '', reminderDays: 30, notifyUserIds: [] })}>
            <Plus className="h-4 w-4 mr-2" /> Add Document
          </Button>
        </div>
      </div>

      {(expiringCount > 0 || expiredCount > 0) && (
        <Card className="mb-6 border-warning/30 bg-warning/5">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertTriangle className="mt-0.5 h-4 w-4 text-warning shrink-0" />
            <div className="text-sm">
              {expiredCount > 0 && <p className="text-destructive font-medium">{expiredCount} document{expiredCount !== 1 ? 's' : ''} expired</p>}
              {expiringCount > 0 && <p className="text-warning">{expiringCount} document{expiringCount !== 1 ? 's' : ''} expiring soon</p>}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-3 mb-5 items-end flex-wrap">
        <Input label="Search" placeholder="Document or assignee..." value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1 max-w-[300px]" />
        {docTypes.length > 0 && (
          <Select label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} options={[{ value: 'all', label: 'All types' }, ...docTypes.map((t) => ({ value: t.rowKey, label: t.displayName }))]} />
        )}
        <Select label="Assignment" value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)} options={[
          { value: 'all', label: 'All' },
          { value: 'org', label: 'Organization' },
          { value: 'personal', label: 'Per-employee' },
        ]} />
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button key={f.value} onClick={() => setFilter(f.value)} className={cn('rounded-md border px-3 py-1.5 text-sm transition-colors cursor-pointer', filter === f.value ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-input text-muted-foreground hover:bg-muted')}>{f.label}</button>
          ))}
        </div>
      </div>

      {/* Documents table */}
      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Document</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Type</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Assigned to</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">File</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Expiry</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Notify</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((doc) => {
                const status = getStatus(doc);
                const days = getDaysUntil(doc.expiryDate);
                return (
                  <tr key={doc.rowKey} className="border-b border-border/50 last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-medium">{doc.documentName || doc.fileName || '—'}</div>
                      {doc.description && <div className="text-xs text-muted-foreground">{doc.description}</div>}
                    </td>
                    <td className="px-4 py-3"><Badge variant="default">{getTypeName(doc.docTypeId || doc.rowKey)}</Badge></td>
                    <td className="px-4 py-3">
                      {doc.partitionKey === 'org' ? (
                        <Badge variant="info">Organization</Badge>
                      ) : doc.partitionKey === 'shared' ? (
                        (() => {
                          const list = Array.isArray(doc.assignedUsers) ? doc.assignedUsers : [];
                          if (list.length === 0) {
                            return <span className="text-xs text-muted-foreground/60">No assignees</span>;
                          }
                          if (list.length === 1) {
                            return (
                              <div>
                                <div className="text-sm">{list[0].displayName || list[0].rowKey}</div>
                                {list[0].email && (
                                  <div className="text-xs text-muted-foreground">{list[0].email}</div>
                                )}
                              </div>
                            );
                          }
                          const preview = list.slice(0, 3).map((u) => u.displayName || u.email).filter(Boolean).join(', ');
                          const extra = list.length - 3;
                          return (
                            <div title={list.map((u) => u.displayName || u.email).filter(Boolean).join('\n')}>
                              <Badge variant="info">Shared · {list.length}</Badge>
                              <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[220px]">
                                {preview}{extra > 0 ? `, +${extra} more` : ''}
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        <div>
                          <div className="text-sm">{doc.userName || doc.partitionKey}</div>
                          {doc.userEmail && <div className="text-xs text-muted-foreground">{doc.userEmail}</div>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {doc.fileName ? (
                        <div className="flex items-center gap-1.5 max-w-[160px]">
                          <span className="text-xs font-mono truncate">{doc.fileName}</span>
                          {doc.fileMissing && (
                            <span title="The file is missing from storage. Re-upload to fix." className="shrink-0">
                              <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground/40">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">{doc.expiryDate || '—'}</td>
                    <td className="px-4 py-3 text-xs">{doc.reminderDays ?? 30}d</td>
                    <td className="px-4 py-3">
                      {doc.fileMissing && <Badge variant="danger">File missing</Badge>}
                      {!doc.fileMissing && status === 'active' && <Badge variant="success">Active</Badge>}
                      {!doc.fileMissing && status === 'no_notifiees' && <Badge variant="warning">No notifiees</Badge>}
                      {!doc.fileMissing && status === 'expiring' && <Badge variant="warning">{days}d left</Badge>}
                      {!doc.fileMissing && status === 'expired' && <Badge variant="danger">Expired</Badge>}
                      {!doc.fileMissing && status === 'none' && <Badge variant="default">No File</Badge>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => setEditing(doc)} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => handleDelete(doc)} className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-muted-foreground">
                  {documents.length === 0 ? 'No documents yet. Click "Add Document" to start tracking.' : 'No documents match your filters.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit modal */}
      {editing && (
        <DocumentModal
          doc={editing}
          docTypes={docTypes}
          users={users}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function DocumentModal({ doc, docTypes, users, onClose, onSaved }) {
  const toast = useToast();
  const [name, setName] = useState(doc.documentName || '');
  const [docTypeId, setDocTypeId] = useState(doc.docTypeId || '');
  const [description, setDescription] = useState(doc.description || '');
  const [expiryDate, setExpiryDate] = useState(doc.expiryDate || '');
  const [reminderDays, setReminderDays] = useState(doc.reminderDays ?? 30);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  // Live upload progress (0-100) while the file is being shipped to
  // the backend. Only meaningful while saving=true and file != null —
  // metadata-only edits skip the upload step entirely and stay at 0.
  const [uploadPct, setUploadPct] = useState(0);

  // Per-document notifiees. Stored as JSON-encoded string[] on the
  // entity; expiryNotifier fans out one personalized email per id.
  const [notifyUserIds, setNotifyUserIds] = useState(() => {
    if (Array.isArray(doc.notifyUserIds)) return doc.notifyUserIds;
    if (typeof doc.notifyUserIds === 'string' && doc.notifyUserIds) {
      try { return JSON.parse(doc.notifyUserIds); } catch { return []; }
    }
    return [];
  });

  // Assignment scope. Editable for both new and existing docs — when
  // the scope/targets change, save() uploads to the new targets and
  // deletes the old row(s). New docs default to "personal with no one
  // selected" so the admin makes an explicit choice (no accidental
  // org-wide pushes).
  const initialScope = doc.isNew
    ? 'personal'
    : (doc.partitionKey === 'org' ? 'org' : 'personal');
  const [scope, setScope] = useState(initialScope);
  // Stored as a string[] of user rowKeys to match the multi-select
  // dropdown component used everywhere else (UserMultiSelect).
  // Three cases when editing:
  //   - doc.partitionKey === 'shared': read the JSON assignedUserIds
  //     array (this is the new single-row-multiple-assignees shape)
  //   - doc.partitionKey === 'org': empty (managed by the scope toggle)
  //   - doc.partitionKey === <userId> (legacy per-user duplicate): seed
  //     with that single userId. Saving with more assignees migrates
  //     the doc into the 'shared' partition (one row going forward).
  const [assignedUserIdList, setAssignedUserIdList] = useState(() => {
    if (doc.isNew || doc.partitionKey === 'org') return [];
    if (doc.partitionKey === 'shared') {
      if (Array.isArray(doc.assignedUserIds)) return doc.assignedUserIds;
      if (typeof doc.assignedUserIds === 'string' && doc.assignedUserIds) {
        try {
          const parsed = JSON.parse(doc.assignedUserIds);
          if (Array.isArray(parsed)) return parsed;
        } catch {}
      }
      return [];
    }
    return [doc.partitionKey];
  });
  const assignedUserIds = useMemo(() => new Set(assignedUserIdList), [assignedUserIdList]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Document name is required'); return; }
    if (scope === 'personal' && assignedUserIds.size === 0) {
      toast.error('Pick at least one employee to assign this document to');
      return;
    }

    setSaving(true);
    setUploadPct(0);
    try {
      const slug = doc.isNew
        ? name.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
        : doc.rowKey;

      // Single upload — even multi-user assignments land in ONE row.
      //   - 'org' partition: organization-wide (existing model)
      //   - 'shared' partition: explicit list in assignedUserIds[]
      //     (ONE row, ONE blob, surfaces to every assignee's My
      //     Documents view). Replaces the old "one row + blob per
      //     assignee" model that wasted storage and made edits drift.
      const target = scope === 'org' ? 'org' : 'shared';
      const oldPartition = doc.isNew ? null : doc.partitionKey;
      const movedPartition = oldPartition && oldPartition !== target;
      const sourceBlobPath = doc.blobPath || '';
      const sourceFileName = doc.fileName || '';

      try {
        const formData = new FormData();
        if (file) formData.append('document', file);
        formData.append('expiryDate', expiryDate);
        formData.append('reminderDays', String(reminderDays));
        formData.append('documentName', name.trim());
        formData.append('docTypeId', docTypeId);
        formData.append('description', description);
        formData.append('notifyUserIds', JSON.stringify(notifyUserIds || []));
        if (target === 'shared') {
          formData.append('assignedUserIds', JSON.stringify([...assignedUserIds]));
        }
        // When migrating from a different partition (e.g. legacy
        // per-user row → shared) and no new file was attached, ask
        // the backend to copy the blob from the source row's path
        // into the new partition's path.
        if (!file && sourceBlobPath && movedPartition) {
          formData.append('sourceBlobPath', sourceBlobPath);
          formData.append('sourceFileName', sourceFileName);
        }
        // Pass an onProgress callback only when there's actually a
        // file being uploaded — metadata-only saves are tiny and the
        // wheel would just flicker.
        const opts = file ? { onProgress: setUploadPct } : undefined;
        await adminApi.uploadDocument(target, slug, formData, opts);
      } catch (err) {
        toast.error(`Save failed: ${err.message}`);
        return;
      }

      // If we migrated the row to a new partition, delete the old
      // row to avoid duplicates. (Org and shared rows live under
      // unique partitionKeys; rowKey is the docTypeId slug.)
      if (movedPartition) {
        try {
          await adminApi.deleteDocument(oldPartition, doc.rowKey);
        } catch (err) {
          console.warn('[document-save] failed to delete old row:', err);
        }
      }

      const assigneeCount = target === 'shared' ? assignedUserIds.size : 0;
      toast.success(
        target === 'org'
          ? `Document "${name}" saved to the organization`
          : `Document "${name}" saved · shared with ${assigneeCount} employee${assigneeCount === 1 ? '' : 's'}`
      );
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={doc.isNew ? 'Add Document' : 'Edit Document'} width="560px">
      <div className="space-y-4">
        <Input label="Document Name *" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Company Insurance Policy" />
        {docTypes.length > 0 && (
          <Select label="Document Type" value={docTypeId} onChange={(e) => setDocTypeId(e.target.value)} options={[{ value: '', label: 'Select type...' }, ...docTypes.map((t) => ({ value: t.rowKey, label: t.displayName }))]} />
        )}
        <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional notes" />

        {/* Assignment — editable inline for new and existing docs.
            Scope is a normal Select; "Specific employees" reveals the
            same multi-select dropdown used for notifiees. Default is
            "Specific employees" + empty list so a doc never lands
            org-wide unless the admin explicitly chooses Organization. */}
        <Select
          label="Assign to"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          options={[
            { value: 'personal', label: 'Specific employees' },
            { value: 'org', label: 'Organization (everyone)' },
          ]}
        />
        {scope === 'personal' && (
          <UserMultiSelect
            label="Employees with access"
            users={users}
            value={assignedUserIdList}
            onChange={setAssignedUserIdList}
            placeholder="Pick one or more employees..."
            helpText="Each selected employee gets their own copy of this document under My Documents."
          />
        )}

        <div className="grid grid-cols-2 gap-3">
          <Input label="Expiry Date" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
          <Input label="Notify (days before)" type="number" min="1" value={reminderDays} onChange={(e) => setReminderDays(parseInt(e.target.value) || 30)} />
        </div>

        <UserMultiSelect
          label="Notify these people about expiry"
          users={users}
          value={notifyUserIds}
          onChange={setNotifyUserIds}
          placeholder="Pick one or more notifiees..."
          helpText="Each selected person gets a personal email and an in-app pop-up when this document is in the reminder window or has expired."
        />
        <div>
          <label className="text-[13px] font-medium text-muted-foreground mb-1.5 block">File</label>
          <label className={cn(
            'flex items-center gap-2 rounded-lg border border-dashed border-border px-4 py-3 transition-all',
            saving ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:border-primary/30 hover:bg-muted/30',
          )}>
            <Upload className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground flex-1 truncate">
              {file ? `${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)` : doc.fileName || 'Choose file...'}
            </span>
            <input type="file" accept=".pdf,.doc,.docx,.jpg,.png" className="hidden" disabled={saving} onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          {file && file.size > 25 * 1024 * 1024 && (
            <p className="mt-1.5 text-xs text-warning flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              Large file ({(file.size / (1024 * 1024)).toFixed(1)} MB) — the upload may take a minute. Please keep this window open until it finishes.
            </p>
          )}
          {saving && file && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground flex items-center gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {uploadPct < 100 ? 'Uploading file...' : 'Finishing save...'}
                </span>
                <span className="font-mono text-muted-foreground">{uploadPct}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-150"
                  style={{ width: `${uploadPct}%` }}
                />
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving
              ? (file ? (uploadPct < 100 ? `Uploading ${uploadPct}%` : 'Finishing...') : 'Saving...')
              : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
